import React from 'react';
import { Box, Typography, TextField, RadioGroup, FormControlLabel, Radio, Button } from '@material-ui/core';
import clsx from 'clsx';
import { useFormik } from 'formik';
import validationSchema, { getDefaultAttributes } from 'forms/buyerOptionalInfoForm';
import { isEmpty } from 'ramda';
import PropTypes from 'prop-types';

import UserPresenter from 'presenters/UserPresenter';
import Icon from 'sharedComponents/Icon';
import { useTranslation } from 'react-i18next';

import PhoneInput from './components/PhoneInput';
import useStyles from './useStyles';

const StyledRadio = props => {
  const classes = useStyles();

  return (
    <Radio
      className={classes.radioButton}
      disableRipple
      color="default"
      checkedIcon={<span className={clsx(classes.radioIcon, classes.checkedIcon)} />}
      icon={<span className={classes.radioIcon} />}
      {...props}
    />
  );
};

const OptionalInfoFrom = props => {
  const { t } = useTranslation();
  const classes = useStyles();
  const { onSubmit, currentUser } = props;

  const initialValues = getDefaultAttributes(currentUser);

  const agreeKeepUpdated = 'agree';
  const disagreeKeepUpdated = 'disagree';
  const initialValueKeepUpdated = initialValues.keepUpdated ? agreeKeepUpdated : disagreeKeepUpdated;

  const formik = useFormik({
    validationSchema,
    initialValues: { ...initialValues, keepUpdated: initialValueKeepUpdated },
    onSubmit,
  });
  const { errors, values, handleChange, handleBlur, touched, handleSubmit } = formik;

  const isAbleToSubmit = isEmpty(errors);

  const checkedAgree = values.keepUpdated === agreeKeepUpdated;
  const checkedDisagree = values.keepUpdated === disagreeKeepUpdated;

  return (
    <Box className={classes.container}>
      <Typography variant="h2" className={classes.title}>
        {t('components.OptionalInfoForm.title')}
      </Typography>
      <form onSubmit={handleSubmit}>
        <Typography variant="body2" className={classes.text}>
          {t('components.OptionalInfoForm.firstText')}
        </Typography>
        <Box className={classes.formGroup}>
          <Box className={classes.fieldGroup}>
            <TextField
              error={touched.firstName && Boolean(errors.firstName)}
              helperText={touched.firstName && t(errors.firstName)}
              variant="outlined"
              label={t('components.OptionalInfoForm.fields.firstName')}
              className={classes.field}
              id="firstName"
              name="firstName"
              autoComplete="firstName"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.firstName}
              type="text"
              InputProps={{
                style: {
                  height: '40px',
                },
              }}
              InputLabelProps={{
                shrink: false,
              }}
            />
            <TextField
              error={touched.lastName && Boolean(errors.lastName)}
              helperText={touched.lastName && t(errors.lastName)}
              variant="outlined"
              label={t('components.OptionalInfoForm.fields.lastName')}
              className={classes.field}
              id="lastName"
              name="lastName"
              autoComplete="lastName"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.lastName}
              type="text"
              InputProps={{
                style: {
                  height: '40px',
                },
              }}
              InputLabelProps={{
                shrink: false,
              }}
            />
            <TextField
              error={touched.phoneNumber && Boolean(errors.phoneNumber)}
              helperText={touched.phoneNumber && t(errors.phoneNumber)}
              variant="outlined"
              label={t('components.OptionalInfoForm.fields.phoneNumber')}
              className={classes.field}
              color="primary"
              placeholder="(123) 456-7890"
              id="phoneNumber"
              name="phoneNumber"
              autoComplete="tel"
              type="text"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.phoneNumber}
              InputProps={{
                style: {
                  height: '40px',
                },
                inputComponent: PhoneInput,
              }}
              InputLabelProps={{
                shrink: false,
              }}
            />

            <TextField
              error={touched.email && Boolean(errors.email)}
              helperText={touched.email && t(errors.email)}
              variant="outlined"
              label={t('components.OptionalInfoForm.fields.email')}
              className={classes.field}
              color="primary"
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.email}
              InputProps={{
                style: {
                  height: '40px',
                },
              }}
              FormHelperTextProps={{
                style: {
                  marginLeft: 0,
                },
              }}
              InputLabelProps={{
                shrink: false,
              }}
            />
          </Box>
        </Box>
        <Typography variant="body2" className={classes.text}>
          {t('components.OptionalInfoForm.secondText')}
        </Typography>
        <RadioGroup name="keepUpdated" onChange={handleChange} className={classes.radioGroup}>
          <FormControlLabel
            className={classes.radioGroupLabel}
            checked={checkedAgree}
            value={agreeKeepUpdated}
            control={<StyledRadio />}
            label={t('components.OptionalInfoForm.checkboxAgreeText')}
          />
          <FormControlLabel
            className={classes.radioGroupLabel}
            checked={checkedDisagree}
            value={disagreeKeepUpdated}
            control={<StyledRadio />}
            label={t('components.OptionalInfoForm.checkboxDisagreeText')}
          />
        </RadioGroup>
        <Button
          variant="contained"
          color="primary"
          className={classes.button}
          startIcon={<Icon name="saveWhite" className={classes.buttonIcon} />}
          disabled={!isAbleToSubmit}
          type="submit"
        >
          {t('components.OptionalInfoForm.button')}
        </Button>
      </form>
    </Box>
  );
};

OptionalInfoFrom.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  currentUser: UserPresenter.shape().isRequired,
};

export default OptionalInfoFrom;



// WEBPACK FOOTER //
// ./src/components/BuyerSettings/components/OptionalInfoForm/OptionalInfoForm.js